import { notify } from '@affine/component';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import type { PropsWithChildren } from 'react';
import { useState } from 'react';

import {
  AuthService,
  defaultWorkspaceApiClient,
  WorkspaceSubscriptionService,
} from '../../../../../modules/cloud';
import { WorkspaceService } from '../../../../../modules/workspace';
import {
  type CancellationDeclarationForm,
  ConfirmLoadingModal,
  DowngradeModal,
  WithdrawalModal,
} from './modals';

interface WithdrawalResponse {
  ok: true;
  receivedAt: string;
  refund: {
    refundAmount: number;
    currency: string;
  };
}

function formatMinorCurrency(
  amount: number,
  currency: string,
  language?: string
) {
  try {
    return new Intl.NumberFormat(
      language?.startsWith('en') ? 'en-US' : 'de-DE',
      {
        style: 'currency',
        currency: currency.toUpperCase(),
      }
    ).format(amount / 100);
  } catch {
    return `${(amount / 100).toFixed(2)} ${currency.toUpperCase()}`;
  }
}

export const CancelTeamAction = ({
  children,
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
} & PropsWithChildren) => {
  const [isMutating, setIsMutating] = useState(false);
  const authService = useService(AuthService);
  const subscription = useService(WorkspaceSubscriptionService).subscription;
  const workspaceSubscription = useLiveData(subscription.subscription$);
  const confirmationEmail = authService.session.account$.value?.email ?? null;
  const t = useI18n();

  const downgrade = useAsyncCallback(
    async (declaration: CancellationDeclarationForm) => {
      try {
        setIsMutating(true);
        const plan = workspaceSubscription?.plan ?? 'Team';
        await subscription.cancelSubscription(plan, {
          language: t.language?.startsWith('en') ? 'en' : 'de',
          terminationReason: declaration.terminationReason,
          terminationType: declaration.terminationType,
        });
        await subscription.waitForRevalidation();
        onOpenChange(false);
      } finally {
        setIsMutating(false);
      }
    },
    [subscription, t, onOpenChange, workspaceSubscription]
  );

  if (workspaceSubscription?.canceledAt) {
    return null;
  }

  return (
    <>
      {children}
      <DowngradeModal
        variant="team"
        planName={workspaceSubscription?.plan}
        confirmationEmail={confirmationEmail}
        open={open}
        onCancel={downgrade}
        onOpenChange={onOpenChange}
        loading={isMutating}
      />
    </>
  );
};

export const TeamResumeAction = ({
  children,
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
} & PropsWithChildren) => {
  const [isMutating, setIsMutating] = useState(false);
  const subscription = useService(WorkspaceSubscriptionService).subscription;
  const workspaceSubscription = useLiveData(subscription.subscription$);
  const t = useI18n();

  const resume = useAsyncCallback(async () => {
    try {
      setIsMutating(true);
      const plan = workspaceSubscription?.plan ?? 'Team';
      await subscription.resumeSubscription(plan);
      await subscription.waitForRevalidation();
      onOpenChange(false);
      notify.success({
        title: t['com.streamlain.payment.resume.success.title'](),
        message: t['com.streamlain.payment.resume.success.team.message'](),
      });
    } finally {
      setIsMutating(false);
    }
  }, [subscription, onOpenChange, workspaceSubscription, t]);

  return (
    <>
      {children}
      <ConfirmLoadingModal
        type={'resume'}
        open={open}
        onConfirm={resume}
        onOpenChange={onOpenChange}
        loading={isMutating}
      />
    </>
  );
};

export const WithdrawalAction = ({
  children,
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
} & PropsWithChildren) => {
  const [isMutating, setIsMutating] = useState(false);
  const authService = useService(AuthService);
  const subscription = useService(WorkspaceSubscriptionService).subscription;
  const workspace = useService(WorkspaceService).workspace;
  const workspaceSubscription = useLiveData(subscription.subscription$);
  const t = useI18n();
  const confirmationEmail = authService.session.account$.value?.email ?? null;

  const submitWithdrawal = useAsyncCallback(async () => {
    try {
      setIsMutating(true);
      const plan = workspaceSubscription?.plan ?? 'Team';
      const response = await defaultWorkspaceApiClient.request({
        workspaceId: workspace.id,
        path: '/api/payment/legal/withdrawal',
        init: {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contractLabel: `${plan} plan`,
            language: t.language?.startsWith('en') ? 'en' : 'de',
            plan,
            workspaceId: workspace.id,
          }),
        },
        category: 'settings',
        key: 'POST:/api/payment/legal/withdrawal',
        mode: 'foreground',
        priority: 'foreground',
      });
      if (!response.ok) {
        throw new Error(await response.text());
      }
      const result = (await response.json()) as WithdrawalResponse;
      notify.success({
        title: t['com.streamlain.payment.withdrawal.success'](),
        message: t['com.streamlain.payment.withdrawal.success.message']({
          amount: formatMinorCurrency(
            result.refund.refundAmount,
            result.refund.currency,
            t.language
          ),
        }),
      });
      await subscription.waitForRevalidation();
      onOpenChange(false);
    } finally {
      setIsMutating(false);
    }
  }, [onOpenChange, subscription, t, workspace.id, workspaceSubscription]);

  return (
    <>
      {children}
      <WithdrawalModal
        confirmationEmail={confirmationEmail}
        loading={isMutating}
        onConfirm={submitWithdrawal}
        onOpenChange={onOpenChange}
        open={open}
        planName={workspaceSubscription?.plan}
      />
    </>
  );
};

export const ContractChangeWithdrawalAction = ({
  changeId,
  children,
  contractLabel,
  onSubmitted,
  open,
  onOpenChange,
}: {
  changeId: string;
  contractLabel: string;
  onSubmitted?: () => Promise<void> | void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
} & PropsWithChildren) => {
  const [isMutating, setIsMutating] = useState(false);
  const authService = useService(AuthService);
  const subscription = useService(WorkspaceSubscriptionService).subscription;
  const workspace = useService(WorkspaceService).workspace;
  const t = useI18n();
  const confirmationEmail = authService.session.account$.value?.email ?? null;

  const submitWithdrawal = useAsyncCallback(async () => {
    try {
      setIsMutating(true);
      const path = `/api/payment/legal/contract-changes/${encodeURIComponent(
        changeId
      )}/withdrawal`;
      const response = await defaultWorkspaceApiClient.request({
        workspaceId: workspace.id,
        path,
        init: {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            language: t.language?.startsWith('en') ? 'en' : 'de',
            workspaceId: workspace.id,
          }),
        },
        category: 'settings',
        key: `POST:${path}`,
        mode: 'foreground',
        priority: 'foreground',
      });
      if (!response.ok) {
        throw new Error(await response.text());
      }
      const result = (await response.json()) as WithdrawalResponse;
      notify.success({
        title: t['com.streamlain.payment.withdrawal.success'](),
        message: formatMinorCurrency(
          result.refund.refundAmount,
          result.refund.currency,
          t.language
        ),
      });
      await subscription.waitForRevalidation();
      await onSubmitted?.();
      onOpenChange(false);
    } finally {
      setIsMutating(false);
    }
  }, [changeId, onOpenChange, onSubmitted, subscription, t, workspace.id]);

  return (
    <>
      {children}
      <WithdrawalModal
        confirmationEmail={confirmationEmail}
        contractLabel={contractLabel}
        loading={isMutating}
        onConfirm={submitWithdrawal}
        onOpenChange={onOpenChange}
        open={open}
      />
    </>
  );
};
